import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronUp,
  Minus,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  ShoppingCart,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  multiply,
  sum,
  type DeviceListResponse,
  type OrderDto,
  type ProductDto,
} from '@gc/shared';
import { api } from '@/lib/api';

import { useAuth } from '@/features/auth/useAuth';
import {
  DestinationPicker,
  orderDestinationFor,
  resolveDeepLinkDestination,
  type Destination,
} from './DestinationPicker';
import { OrderTicketsDialog } from './OrderTicketsDialog';
import { isSoldOut } from './soldOut';
import { EMPTY_PRODUCT_FILTERS, submitOrder, useProductCategories, useProducts } from './useCafe';
import { ApiError } from '@/lib/api';

/** `orderLineSchema`'s cap. Clipped at submit so an essay cannot fail the order. */
const MAX_ITEM_NOTE = 200;

interface CartLine {
  product: ProductDto;
  quantity: number;
  /**
   * What the kitchen is told about THIS item — "no pickles", "extra hot".
   *
   * Per line, not per order, and that is the whole point: an order of a burger
   * and a pizza has one note box per row, so "no olives" cannot end up attached
   * to the burger. It prints in the Notes column beside its own item, which is
   * where a cook looks; the single order-wide note this replaced printed at the
   * foot of the ticket, detached from anything.
   */
  note: string;
}

/**
 * Cafe ordering (SPEC §5.4).
 *
 * Browse the menu by category with images and prices, build an order, choose a
 * destination — a table, a device, or the tab as a whole — and submit. The
 * server splits the order into one ticket per station and queues the print
 * jobs; the dialog that follows shows exactly what each station received.
 */
export function CafeOrderPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /*
   * Phone only: the category chips hide behind one "Filters" button — on a
   * 375px screen the full strip was half the viewport before the first
   * product appeared. Desktop never collapses.
   */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cart, setCart] = useState<CartLine[]>([]);
  /*
   * "We sent two burgers, they ordered one." Ticking this turns the SAME pad
   * into a return: the tab is credited, the stock comes back, and the station
   * gets a chit headed RETURN reading `Qty = -1`.
   *
   * Deliberately part of the ordinary order pad rather than a separate screen —
   * the mistake is noticed at the till, with the menu already open, and a
   * correction that needs navigating to is a correction that gets postponed
   * until nobody remembers what went out.
   */
  const [isReturn, setIsReturn] = useState(false);
  /*
   * Pressing Order on a device arrives here with that device — and, when
   * somebody is playing on it, their tab — already in the URL. Honour it: the
   * cashier pressed a specific machine, and making them find that party again
   * in a list of forty is the whole reason this was worth fixing.
   *
   * The link carries BOTH when somebody is playing: `?device=PC-14&tab=…`.
   * The tab pre-picks the party; the device is what the station ticket heads
   * itself with, so it has to be resolved even though the tab is already known.
   *
   * That last sentence is the bug this used to have. The lookup was skipped
   * whenever the URL already carried a tab — which is every occupied machine,
   * i.e. every order anybody actually places — so the device was dropped and
   * the chit said "Tab" over a machine the cashier had pressed by name.
   */
  const [params] = useSearchParams();
  const [destination, setDestination] = useState<Destination>(() => ({
    tabId: params.get('tab'),
  }));

  const deepLinkDeviceId = params.get('device');
  /** The deep link is resolved ONCE; after that the picker is the cashier's. */
  const deepLinkResolved = useRef(false);
  useEffect(() => {
    if (!deepLinkDeviceId || deepLinkResolved.current) return;
    deepLinkResolved.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const { devices } = await api.get<DeviceListResponse>('/devices');
        const device = devices.find((d) => d.id === deepLinkDeviceId);
        if (cancelled || !device) return;

        setDestination((current) => resolveDeepLinkDestination(current, device));
      } catch {
        // The picker still works by hand; the deep link was only a shortcut.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolve once, on arrival
  }, []);
  /*
   * Phone only: the order pad is a bottom sheet. Collapsed it is one bar —
   * item count, total, chevron — so the MENU gets the whole screen while
   * browsing; every tap on a product updates the bar, which is the feedback.
   * Desktop ignores this state entirely.
   */
  const [padOpen, setPadOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState<OrderDto | null>(null);

  const { categories } = useProductCategories();
  // Menu mode: only sellable items, and search runs server-side so a barcode
  // scan into the same box lands on one product.
  const { data, loading, reload } = useProducts({
    ...EMPTY_PRODUCT_FILTERS,
    onlyActive: true,
    productCategoryId: categoryId,
    search,
  });

  const products = data?.products ?? [];

  const subtotal = useMemo(
    () => sum(cart.map((line) => multiply(fromMillidinars(line.product.priceMd), line.quantity))),
    [cart],
  );

  const canOrder = can('cafe.order');
  // Returns move money the customer's way, so they carry their own audited
  // permission. Someone without it never sees the checkbox at all.
  const canReturn = can('cafe.return');

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';

  return (
    // Side-by-side from `md`; below it the pad slides UNDER the menu instead —
    // a 320px pad beside a 375px screen left 55px for the products. A phone on
    // its side (780–930px wide) keeps the pad BESIDE the menu: width is what it
    // has to spare. Height it has not, which is the pad's `short` layout below.
    <div className="flex h-full min-h-0 max-md:flex-col">
      {/* ---- Menu -------------------------------------------------------- */}
      <div
        className={`flex min-h-0 min-w-0 flex-1 flex-col ${padOpen ? 'max-md:short:hidden' : ''}`}
      >
        {/* A phone on its side has ~320px under the top bar, and this header
            wrapped to two rows there (118px at 812×375, with Manage products
            showing). Short, it keeps to one row: less padding, a narrower
            search box, and Manage products as its icon alone. */}
        <header className="flex flex-wrap items-center gap-3 p-4 compact:short:py-2">
          <h1 className="text-xl font-semibold text-content">{t('cafe.title')}</h1>

          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
              style={{ insetInlineStart: '0.6rem' }}
              aria-hidden
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('cafe.searchPlaceholder')}
              className={`${controlClass} w-56 ps-8 compact:short:w-40`}
            />
          </div>

          {/* Phone only: the chips below collapse behind this one button. */}
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            className={`inline-flex min-h-10 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors roomy:hidden ${
              categoryId !== null
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-subtle text-content-secondary hover:bg-hover'
            }`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            {categoryId !== null
              ? (categories.find((c) => c.id === categoryId)?.name ?? t('cafe.filters'))
              : t('cafe.filters')}
          </button>

          <div className="flex-1" />

          {can('products.manage') ? (
            <Link
              to="/cafe/products"
              className="inline-flex items-center gap-1 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
            >
              <Settings2 className="h-4 w-4" aria-hidden />
              <span className="compact:short:sr-only">{t('cafe.manageProducts')}</span>
            </Link>
          ) : null}
        </header>

        {/* Category strip — the menu sections, Arabic names included. On a
            phone it exists only while "Filters" is open; picking a category
            closes it again so the products get the screen back. Open, it is
            capped at 45% of the visible height and scrolls inside itself, so
            forty sections never push the products off the screen.

            45% of the WHOLE height is only enough upright. On a phone on its
            side the top bar and this header take most of the rest, and the
            grid under an open strip was left 32px — less than one tile
            (measured at 812×375, 2026-09-23). So there the grid keeps a floor
            of one full row of tiles (`compact:short:min-h-38` below), and the
            strip, a scroll container free to shrink, gets what is left. */}
        <div
          className={`flex-wrap gap-2 px-4 pb-3 roomy:flex compact:max-h-[45dvh] compact:overflow-y-auto compact:overscroll-contain ${filtersOpen ? 'flex' : 'hidden'}`}
        >
          <CategoryChip
            label={t('cafe.allCategories')}
            active={categoryId === null}
            onClick={() => {
              setCategoryId(null);
              setFiltersOpen(false);
            }}
          />
          {categories.map((category) => (
            <CategoryChip
              key={category.id}
              label={category.name}
              active={categoryId === category.id}
              onClick={() => {
                setCategoryId(category.id);
                setFiltersOpen(false);
              }}
            />
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4 compact:short:min-h-38">
          {loading && !data ? (
            <p className="text-sm text-content-muted">{t('common.loading')}</p>
          ) : products.length === 0 ? (
            <p className="text-sm text-content-muted">{t('common.noResults')}</p>
          ) : (
            <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(9rem,1fr))]">
              {products.map((product) => (
                <MenuTile
                  key={product.id}
                  product={product}
                  currencySymbol={currencySymbol}
                  disabled={!canOrder}
                  isReturn={isReturn}
                  onAdd={() => addToCart(product)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Order pad --------------------------------------------------- */}
      {/* The bottom sheet opens to 70% of the height. On a narrow SHORT
          screen — an iPhone 8 or SE on its side, 667×375 — 70% left one cart
          line, so there it takes the whole page and the menu steps aside
          until the bar shuts it again. */}
      <aside
        className={`flex w-80 shrink-0 flex-col border-s border-subtle bg-raised max-md:w-full max-md:border-s-0 max-md:border-t ${
          padOpen
            ? 'max-md:max-h-[70dvh] max-md:short:min-h-0 max-md:short:max-h-none max-md:short:flex-1'
            : ''
        }`}
      >
        <h2 className="border-b border-subtle p-4 text-sm font-semibold text-content max-md:hidden short:hidden">
          {t('cafe.currentOrder')}
        </h2>

        {/* The bar IS the pad while collapsed. Always on screen, always live. */}
        <button
          type="button"
          onClick={() => setPadOpen((open) => !open)}
          aria-expanded={padOpen}
          className="flex min-h-12 items-center gap-2.5 px-4 text-start md:hidden"
        >
          <ShoppingCart className="h-4 w-4 shrink-0 text-content-secondary" aria-hidden />
          <span className="text-sm font-medium text-content">
            {t('cafe.orderBarItems', { times: cart.reduce((n, line) => n + line.quantity, 0) })}
          </span>
          <span className="tabular ms-auto text-sm font-semibold text-content">
            {formatIqd(subtotal, { currencySymbol })}
          </span>
          <ChevronUp
            className={`h-4 w-4 shrink-0 text-content-secondary transition-transform ${padOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>

        {/* Lines, visitor and return as ONE scroller. Tall, the lines scroll
            inside it and the visitor picker stays pinned under them, as it
            always has. SHORT — a phone on its side has ~320px under the top
            bar — that split gave the lines 0px and pushed Send off the screen
            (measured at 812×375, 2026-09-23), so there the lines take their
            natural height and the whole block scrolls. `min-h-24` keeps a
            line and a half in view on a small upright phone too. */}
        <div
          className={`flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain ${padOpen ? '' : 'max-md:hidden'}`}
        >
          <div className="min-h-24 flex-1 overflow-auto p-3 short:min-h-0 short:flex-none short:overflow-visible">
            {cart.length === 0 ? (
              <p className="text-sm text-content-muted">{t('cafe.emptyOrder')}</p>
            ) : (
              /* Flat ticket lines on hairlines (card `.line`) — the pad reads
               as the receipt it is about to become, not a stack of boxes. */
              <ul>
                {cart.map((line) => (
                  <li key={line.product.id} className="border-b border-subtle py-2 text-[12.5px]">
                    <div className="flex items-center gap-2">
                      <div className="flex shrink-0 items-center gap-1">
                        <StepButton
                          label={t('cafe.decrease')}
                          onClick={() => changeQuantity(line.product.id, -1)}
                        >
                          <Minus className="h-3 w-3" aria-hidden />
                        </StepButton>
                        <span className="tabular w-5 text-center text-xs font-semibold text-primary">
                          {line.quantity}
                        </span>
                        <StepButton
                          label={t('cafe.increase')}
                          onClick={() => changeQuantity(line.product.id, 1)}
                        >
                          <Plus className="h-3 w-3" aria-hidden />
                        </StepButton>
                      </div>
                      <span className="min-w-0 flex-1 truncate text-content">
                        {line.product.name}
                      </span>
                      <span className="tabular shrink-0 text-xs font-semibold text-content">
                        {formatIqd(multiply(fromMillidinars(line.product.priceMd), line.quantity), {
                          currencySymbol,
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeLine(line.product.id)}
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                        className="shrink-0 text-content-muted hover:text-danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>

                    {/* Indented under its item and quiet until used: a note box on
                      every row would otherwise be the loudest thing in the pad,
                      on an order where most lines never carry one. */}
                    <input
                      value={line.note}
                      maxLength={200}
                      onChange={(event) => setLineNote(line.product.id, event.target.value)}
                      placeholder={t('cafe.itemNotePlaceholder')}
                      aria-label={t('cafe.itemNoteLabel', { name: line.product.name })}
                      className={`mt-1.5 w-full rounded-control border bg-input px-2 py-1 text-[11.5px] text-content outline-none placeholder:text-content-muted focus:border-primary ${
                        line.note.trim() ? 'border-primary/50' : 'border-subtle'
                      }`}
                      style={{ marginInlineStart: '0.25rem' }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-3 border-t border-subtle p-3">
            <DestinationPicker value={destination} onChange={setDestination} />

            {/* The order-wide note box used to live here. It is gone on purpose:
              it printed at the FOOT of the ticket, detached from any item, so a
              two-item order could not say which one the note was about. Notes
              are per line now — see `CartLine.note`. */}

            {canReturn ? (
              <label
                className={`flex min-h-11 cursor-pointer items-center gap-2.5 rounded-control border px-3 ${
                  isReturn ? 'border-danger bg-danger/10' : 'border-subtle'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isReturn}
                  onChange={(event) => setIsReturn(event.target.checked)}
                  className="h-4 w-4 accent-[var(--status-danger)]"
                />
                <RotateCcw
                  className={`h-4 w-4 shrink-0 ${isReturn ? 'text-danger' : 'text-content-secondary'}`}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span
                    className={`block text-sm font-medium ${isReturn ? 'text-danger' : 'text-content'}`}
                  >
                    {t('cafe.return.label')}
                  </span>
                  <span className="block text-xs text-content-secondary">
                    {t('cafe.return.hint')}
                  </span>
                </span>
              </label>
            ) : null}
          </div>
        </div>

        {/* Total and Send: pinned below the scroller at every height, so the
            one action on this screen is never scrolled out of reach. */}
        <div className={`space-y-3 px-3 pb-3 ${padOpen ? '' : 'max-md:hidden'}`}>
          {/* The ticket total (card `.total`): overline label, 24px display
              numerals in cyan — the figure the customer is quoted before the
              order goes to the printers.

              A return shows the SAME figure negated and in danger red: the
              number on the pad has to be the number that hits the tab, or the
              cashier is agreeing to something they were not shown. */}
          <div className="flex items-baseline justify-between border-t border-strong pt-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-content-muted">
              {isReturn ? t('cafe.return.total') : t('common.total')}
            </span>
            <span
              className={`tabular text-2xl font-bold ${
                isReturn ? 'text-danger' : 'text-primary [text-shadow:var(--glow-primary)]'
              }`}
            >
              {isReturn ? '−' : ''}
              {formatIqd(subtotal, { currencySymbol })}
            </span>
          </div>

          {error ? (
            <p role="alert" className="text-xs text-danger">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canOrder || cart.length === 0 || submitting || !destinationReady()}
            title={canOrder ? undefined : t('cafe.noOrderPermission')}
            // Send order is this screen's one glowing action (spec R3); the
            // glow drops when it is disabled so a dead button never advertises.
            //
            // A return takes the danger colour and drops the glow: it is the
            // same button doing the opposite thing, and it must not look like
            // the habitual one.
            className={`w-full rounded-full px-4 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none ${
              isReturn
                ? 'bg-danger text-white hover:bg-danger-hover'
                : 'bg-primary text-primary-fg shadow-[var(--glow-primary)] hover:bg-primary-hover'
            }`}
          >
            {submitting
              ? t('cafe.sending')
              : isReturn
                ? t('cafe.return.send')
                : t('cafe.sendOrder')}
          </button>
        </div>
      </aside>

      {placed ? (
        <OrderTicketsDialog
          order={placed}
          currencySymbol={currencySymbol}
          onClose={() => setPlaced(null)}
        />
      ) : null}
    </div>
  );

  function destinationReady(): boolean {
    return destination.tabId !== null;
  }

  function addToCart(product: ProductDto): void {
    setCart((current) => {
      const existing = current.find((line) => line.product.id === product.id);
      if (existing) {
        return current.map((line) =>
          line.product.id === product.id ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [...current, { product, quantity: 1, note: '' }];
    });
  }

  /** One line's note. Trimmed and dropped at submit, never here — a cashier
      mid-word must not have the space they just typed taken away. */
  function setLineNote(productId: string, note: string): void {
    setCart((current) =>
      current.map((line) => (line.product.id === productId ? { ...line, note } : line)),
    );
  }

  function changeQuantity(productId: string, delta: number): void {
    setCart((current) =>
      current.flatMap((line) => {
        if (line.product.id !== productId) return [line];
        const quantity = line.quantity + delta;
        return quantity <= 0 ? [] : [{ ...line, quantity }];
      }),
    );
  }

  function removeLine(productId: string): void {
    setCart((current) => current.filter((line) => line.product.id !== productId));
  }

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      // Names the machine the order was placed from, when it was placed from
      // one, so the station ticket can head itself "PC-14" instead of only the
      // party's name. See `orderDestinationFor`.
      const addressed = orderDestinationFor(destination);

      const order = await submitOrder({
        destinationType: addressed.destinationType,
        tabId: destination.tabId,
        destinationDeviceId: addressed.destinationDeviceId,
        // Quantities stay POSITIVE on the wire whichever this is; the server
        // applies the sign from `isReturn`, so one order can never be half
        // sale and half return.
        //
        // Notes are trimmed HERE and empty becomes null: the kitchen ticket must
        // not print a blank Notes cell, and whitespace is not an instruction.
        items: cart.map((line) => {
          const note = line.note.trim().slice(0, MAX_ITEM_NOTE);
          return {
            productId: line.product.id,
            quantity: line.quantity,
            note: note.length > 0 ? note : null,
          };
        }),
        note: null,
        isReturn,
      });
      setPlaced(order);
      setCart([]);
      setPadOpen(false);
      // Cleared with the cart, and on purpose: leaving it ticked would make
      // the NEXT order a return nobody meant.
      setIsReturn(false);
      // The order just moved stock. Without this reload of the menu, the Sold
      // out and "N left" badges stayed as they were when the page opened.
      void reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('cafe.couldNotSend'));
    } finally {
      setSubmitting(false);
    }
  }
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    // The card's chip: an ARMED outline, never a filled button — filled cyan
    // is reserved for this screen's one action, Send order.
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors compact:min-h-10 ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-subtle text-content-secondary hover:bg-hover'
      }`}
    >
      {label}
    </button>
  );
}

function MenuTile({
  product,
  currencySymbol,
  disabled,
  isReturn,
  onAdd,
}: {
  product: ProductDto;
  currencySymbol: string;
  disabled: boolean;
  /** A return puts stock back, so nothing is sold out while one is being rung up. */
  isReturn: boolean;
  onAdd: () => void;
}) {
  const { t } = useTranslation();
  // The server's rule, not a second opinion — see `isSoldOut`. A "No stock"
  // product (trackStock off) still never reads sold out: that is the whole
  // promise of the switch.
  const soldOut = isSoldOut(product, { isReturn });

  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={disabled || soldOut}
      title={soldOut ? t('cafe.soldOut') : undefined}
      className="group flex flex-col overflow-hidden rounded-card border border-subtle bg-raised text-start transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
    >
      <div
        className="grid h-[4.5rem] w-full shrink-0 place-items-center bg-input"
        // The card's placeholder wash: soft brand-violet and brand-cyan pools
        // behind the photo area, via the venue's own accent variables.
        style={
          product.imageUrl
            ? undefined
            : {
                backgroundImage:
                  'radial-gradient(circle at 30% 40%, color-mix(in srgb, var(--brand-secondary) 22%, transparent), transparent 60%),' +
                  'radial-gradient(circle at 70% 60%, color-mix(in srgb, var(--brand-primary) 16%, transparent), transparent 60%)',
              }
        }
      >
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          // No built-in artwork for food: the first letters of the product's
          // own name read correctly in Arabic, Kurdish and English alike.
          <span className="px-2 text-center text-base font-semibold text-content-muted">
            {product.name.slice(0, 2)}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col px-2.5 pb-2 pt-1.5">
        <span className="line-clamp-2 text-[12.5px] font-semibold text-content">
          {product.name}
        </span>
        {/* Price in the display numerals, brand cyan (card `.prod .pr`). */}
        <span className="tabular mt-auto pt-0.5 text-[11.5px] font-semibold text-primary">
          {formatIqd(fromMillidinars(product.priceMd), { currencySymbol })}
        </span>
        {soldOut ? (
          <span className="text-xs text-danger">{t('cafe.soldOut')}</span>
        ) : product.isLowStock ? (
          <span className="text-xs text-ending">
            {t('cafe.lowStockLeft', { count: product.stockQuantity })}
          </span>
        ) : null}
      </div>
    </button>
  );
}

function StepButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      // The card's stepper: a 22px SQUARE key (radius 6), reading as a keypad
      // rather than two more pills beside the pill buttons.
      className="grid h-[22px] w-[22px] place-items-center rounded-[6px] border border-subtle bg-input text-content-secondary hover:bg-hover hover:text-content compact:h-8 compact:w-8"
    >
      {children}
    </button>
  );
}
